import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  useGetOrgQuery,
  useGetOrgCapabilitiesQuery,
  useListFeaturesQuery,
} from '@proaxiv/server-api'
import type { GetOrganizationResponse } from '@proaxiv/server-api'
import { getCountryName } from '../utils/countries'
import { ButtonLink } from '../components/Button'

/** Typed wrapper around the loosely-typed ListFeaturesApiResponse. */
interface ListFeaturesData {
  success: boolean
  features: { id: string }[]
}

/** Id of the event-planner feature, matching the sidebar/`useRequireEventPlannerFeature` gate. */
const EVENT_PLANNER_FEATURE_ID = 'eventplanner'

// ── Icons ────────────────────────────────────────────────────────────────────

const EditOrgIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

const LocationIcon = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
)

const GlobeIcon = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
)

const CalendarIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
    <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" />
  </svg>
)

// ── Page ─────────────────────────────────────────────────────────────────────

interface OrgDetailViewProps {
  orgId: string
}

/**
 * Shared organization detail view used by both the home page (current org) and
 * the `/organizations/:id` route. Accepts `orgId` as a prop rather than reading
 * from `useParams` so it can be driven from any source.
 *
 * Edit / management controls are only shown when the current user is the org owner.
 */
export function OrgDetailView({ orgId }: OrgDetailViewProps) {
  const { t, i18n } = useTranslation()
  const [logoError, setLogoError] = React.useState(false)

  const {
    data: rawOrg,
    isLoading: isOrgLoading,
    isError: isOrgError,
  } = useGetOrgQuery({ orgId }, { skip: !orgId })
  const org = rawOrg as GetOrganizationResponse | undefined

  const { data: orgCaps } = useGetOrgCapabilitiesQuery({ orgId }, { skip: !orgId })

  // Keyed on the `orgId` prop (not the active-org context) so this stays correct on the
  // `/organizations/:id` route too, which can point at a different org than the active one.
  const { data: featuresRaw } = useListFeaturesQuery({ orgId }, { skip: !orgId })
  const enabledFeatureIds =
    (featuresRaw as ListFeaturesData | undefined)?.features?.map((f) => f.id) ?? []
  const hasEventPlannerFeature = enabledFeatureIds.includes(EVENT_PLANNER_FEATURE_ID)

  if (isOrgLoading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-6 md:px-6 md:py-8">
        <p className="text-pax-gray text-sm">{t('loading')}</p>
      </div>
    )
  }

  if (isOrgError || !org?.success) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-6 md:px-6 md:py-8">
        <div className="border-pax-purple/20 flex flex-col items-center gap-4 rounded-2xl border bg-white p-12 text-center">
          <p className="text-pax-gray text-5xl font-black opacity-20">404</p>
          <p className="text-pax-black text-lg font-semibold">{t('org_not_found_title')}</p>
          <p className="text-pax-gray text-sm">{t('org_not_found_desc')}</p>
        </div>
      </div>
    )
  }

  const canUpdateOrg = orgCaps?.canUpdateOrg ?? false
  const orgName = org.name ?? ''
  const orgDescription = org.description ?? ''

  return (
    <>
      <div className="mx-auto max-w-5xl space-y-6 px-4 py-6 md:px-6 md:py-8">
        {/* ── Status banner — shown when org is pending or rejected ── */}
        {org.status === 'PENDING' && (
          <div className="flex items-center gap-3 rounded-xl border border-yellow-300 bg-yellow-50 px-4 py-3">
            <span className="h-2 w-2 shrink-0 rounded-full bg-yellow-400" />
            <p className="text-sm font-medium text-yellow-800">
              {t('org_detail_status_pending_banner')}
            </p>
          </div>
        )}
        {org.status === 'REJECTED' && (
          <div className="flex items-center gap-3 rounded-xl border border-red-300 bg-red-50 px-4 py-3">
            <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" />
            <p className="text-sm font-medium text-red-800">
              {t('org_detail_status_rejected_banner')}
            </p>
          </div>
        )}
        {/* First-time users who were just approved had no clue where to go next (issue #203) —
            an accepted org with zero features enabled gets the same yellow treatment as the
            pending banner, so the "nothing is activated yet" state never reads as a dead page. */}
        {org.status === 'ACCEPTED' && enabledFeatureIds.length === 0 && (
          <div className="flex items-center gap-3 rounded-xl border border-yellow-300 bg-yellow-50 px-4 py-3">
            <span className="h-2 w-2 shrink-0 rounded-full bg-yellow-400" />
            <p className="text-sm font-medium text-yellow-800">
              {t('org_detail_status_no_features_banner')}
            </p>
          </div>
        )}

        {/* Once a feature is live, replace the "nothing is activated" messaging with a direct,
            unmissable way in — this is the big button DEV-RESOLUTION asked for "where previously
            was the pending banner" so it stays the first thing a returning user sees. */}
        {hasEventPlannerFeature && (
          <ButtonLink
            to="/event-planner"
            variant="primary"
            size="lg"
            fullWidth
            leftIcon={<CalendarIcon />}
          >
            {t('org_detail_go_to_event_planner')}
          </ButtonLink>
        )}

        {/* ── Edit action — sits above the hero card so it reads as a page-level action
            rather than something tied to the name/logo it used to share a row with. */}
        {canUpdateOrg && (
          <div className="flex justify-end">
            <Link
              to={`/organizations/${orgId}/edit`}
              className="text-pax-blue border-pax-blue/30 hover:bg-pax-light-blue flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-medium transition-colors focus-visible:outline-none"
            >
              {t('org_detail_tab_edit')}
              <EditOrgIcon />
            </Link>
          </div>
        )}

        {/* ── Company hero card ── */}
        <div className="border-pax-purple/20 flex flex-row items-start gap-4 rounded-2xl border bg-white p-5 sm:gap-6 sm:p-6">
          <div className="border-pax-purple/20 bg-pax-light-purple flex h-[72px] w-[72px] flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border sm:h-[120px] sm:w-[120px]">
            {org?.logoUrl && !logoError ? (
              <img
                key={org.logoUrl}
                src={org.logoUrl}
                alt={orgName || 'Organization logo'}
                className="h-full w-full object-contain p-2"
                onError={() => {
                  setLogoError(true)
                }}
              />
            ) : (
              <img
                src="/default-org-logo.png"
                alt="Organization"
                className="h-full w-full object-contain p-2"
                onError={(e) => {
                  ;(e.target as HTMLImageElement).style.display = 'none'
                }}
              />
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <h1 className="font-display text-pax-black min-w-0 text-xl leading-tight font-bold break-words sm:text-2xl md:text-3xl">
              {orgName}
            </h1>
          </div>
        </div>

        {/* ── Company info card ── */}
        <div className="border-pax-purple/30 flex flex-col gap-3 rounded-2xl border bg-white p-6">
          <p className="text-pax-gray text-xs font-semibold tracking-widest uppercase">
            {t('org_detail_info_label')}
          </p>
          <div className="divide-pax-purple/20 flex flex-col divide-y">
            {org.address && (
              <div className="text-pax-black flex items-center gap-2 py-2.5">
                <LocationIcon />
                <span className="min-w-0 flex-1 text-xs font-medium break-words">
                  <span className="font-bold">{t('org_detail_info_address')}:</span>{' '}
                  {/* Postal code (〒-prefixed, Japanese convention) → region → city → street →
                      building → additional — all on one line rather than the previous
                      street-first layout, to match the postal-mail-style order used on the
                      registration form. */}
                  {[
                    org.address.postalCode && `〒${org.address.postalCode}`,
                    org.address.region,
                    org.address.city,
                    org.address.street,
                    org.address.building,
                    org.address.additional,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                </span>
              </div>
            )}
            {org.address?.country && (
              <div className="text-pax-black flex items-center gap-2 py-2.5">
                <GlobeIcon />
                <span className="min-w-0 flex-1 text-xs font-medium break-words">
                  <span className="font-bold">{t('new_org_field_country')}:</span>{' '}
                  {getCountryName(org.address.country, i18n.language)}
                </span>
              </div>
            )}
            {orgDescription && (
              <div className="text-pax-black flex items-start gap-2 py-2.5">
                {/* No label prefix here (unlike the other rows) — the bio reads as plain
                    free-form text, not a "field: value" pair. */}
                <span className="min-w-0 flex-1 text-xs font-medium break-words">
                  {orgDescription}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
/** Route component for `/organizations/:id` — thin wrapper around {@link OrgDetailView}. */
export function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>()
  return <OrgDetailView orgId={id!} />
}
